import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Button,
  CircularProgress,
  Pagination,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import keyBy from 'lodash/keyBy'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'

import useListLocationDetails from '../../dataFetchHooks/useListLocationDetails'
import useListLocationGroupDetails from '../../dataFetchHooks/useListLocationGroupDetails'
import useListLocationMetricValues from '../../dataFetchHooks/useListLocationMetricValues'
import useListLocationsInEachLocationGroup from '../../dataFetchHooks/useListLocationsInEachLocationGroup'
import BonusPotentialTable from './components/BonusPotentialTable'
import DisqualifiersSection from './components/DisqualifiersSection'
// Import components
import MetricsTable from './components/MetricsTable'
import {
  ROWS_PER_PAGE,
  laborVarianceMetricCodes,
  metricCodes,
} from './constants'
import { downloadCSV } from './csvExport'
import { processMetricsData } from './dataProcessing'
// Import types and constants
import { IAiBlackboxProps, LocationMetricRow, Order, OrderBy } from './types'
import useVariableLaborCoveredEndDate from './useVariableLaborCoveredEndDate'
// Import utilities
import {
  adjustEndDate,
  getTargetVersion,
  getUpdateDate,
  sortTableData,
} from './utils'

const MwbBonusTracker: React.FC<IAiBlackboxProps> = ({
  startDate = '',
  endDate = '',
  queryFilter,
}) => {
  const targetVersion = getTargetVersion(startDate)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<OrderBy>(
    'bonus_attainment_percent_quarter',
  )
  const [page, setPage] = useState(1)
  const [showOtherMarketLeaders, setShowOtherMarketLeaders] = useState(false)

  // Fetch location details
  const { data: locationsMap, isLoading: isLoadingLocations } =
    useListLocationDetails()

  const { data: locationGroupsMap, isLoading: isLoadingLocationGroups } =
    useListLocationGroupDetails()

  const marketLeaders = Object.values(locationGroupsMap ?? {}).filter(
    ({ type }) => type === 'Market Leader',
  )
  const marketLeadersLocationGroupsMap = keyBy(marketLeaders, 'id')
  const marketLeaderLocationGroupIds = marketLeaders.map(({ id }) => id)

  // Adjust end date if it's after today
  const adjustedEndDate = adjustEndDate(endDate)

  // Determine if we should use the new rounding logic (for dates on or after 2026-04-04)
  const useNewRounding = adjustedEndDate >= '2026-04-04'

  const effectiveQueryFilter = queryFilter || {
    isBreakdownByLocationGroup: false,
    locationIds: [],
  }

  // why: variable_labor posts one weekly row on week-ending Fridays while
  // net_sales is daily, so a labor_variance query extending past the last
  // posted labor week mixes a partial-week labor numerator with a full-range
  // sales denominator and flips the metric's sign (ENG-5468). labor_variance
  // is therefore queried separately with its range clamped to the last posted
  // labor day. The other metrics MUST keep the unclamped range: the quarterly
  // bonus metrics are stored only on the latest past Friday of the quarter
  // (kit-core normalize/bonus erases earlier Fridays), so clamping the whole
  // range past that Friday would zero out every bonus column.
  const { coveredEndDate, isLoading: isLoadingLaborCoverage } =
    useVariableLaborCoveredEndDate(
      startDate,
      adjustedEndDate,
      effectiveQueryFilter,
    )

  // why: '' skips the labor_variance queries (the hook skips on empty
  // endDate) while coverage is resolving, and when no posted labor week falls
  // inside the requested range (zero coverage) - Labor Variance then renders
  // blank instead of a value computed from an empty labor numerator.
  const laborEndDate = isLoadingLaborCoverage ? '' : coveredEndDate ?? ''

  // Fetch location mappings for all market leaders
  const { data: locationGroupMappings, isLoading: isLoadingLocationMappings } =
    useListLocationsInEachLocationGroup(
      startDate,
      adjustedEndDate,
      marketLeaderLocationGroupIds,
    )

  const { data: metricsData, isLoading: isLoadingMetrics } =
    useListLocationMetricValues(
      startDate,
      adjustedEndDate,
      effectiveQueryFilter,
      metricCodes,
    )

  const { data: laborMetricsData, isLoading: isLoadingLaborMetrics } =
    useListLocationMetricValues(
      startDate,
      laborEndDate,
      effectiveQueryFilter,
      laborVarianceMetricCodes,
    )

  const locationGroupId = queryFilter?.locationGroupIds?.[0] ?? null

  // Check if current selection is a market leader
  const isMarketLeaderSelected =
    locationGroupId && marketLeaderLocationGroupIds.includes(locationGroupId)

  // We need to call hooks for ALL market leaders (up to 10) to maintain hook order consistency
  const maxMarketLeaders = 10
  const marketLeaderHooksArray = []

  // Call hooks for all potential market leader slots
  for (let i = 0; i < maxMarketLeaders; i++) {
    const mlId = marketLeaderLocationGroupIds[i] // Could be undefined if fewer than 10 market leaders
    const isOtherMarketLeader = mlId && mlId !== locationGroupId
    const shouldFetchThisML = isOtherMarketLeader && isMarketLeaderSelected

    // Get location IDs for this market leader from the mapping
    const mlLocationIds =
      locationGroupMappings?.[mlId]?.locations.map((loc) => loc.id) || []

    const mlQueryFilter = shouldFetchThisML
      ? {
          locationGroupIds: [mlId],
          locationIds: mlLocationIds, // Now we have the location IDs!
          isBreakdownByLocationGroup: false,
        }
      : { isBreakdownByLocationGroup: false, locationIds: [] }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const hook = useListLocationMetricValues(
      startDate,
      adjustedEndDate,
      mlQueryFilter,
      shouldFetchThisML ? metricCodes : [],
    )

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const laborHook = useListLocationMetricValues(
      startDate,
      laborEndDate,
      mlQueryFilter,
      shouldFetchThisML ? laborVarianceMetricCodes : [],
    )

    // Store the hook data for this position
    marketLeaderHooksArray[i] = {
      id: mlId,
      data: hook.data,
      laborData: laborHook.data,
      isLoading: hook.isLoading || laborHook.isLoading,
      isOther: isOtherMarketLeader,
      shouldFetch: shouldFetchThisML,
    }
  }

  // Filter to only get the other market leaders that should be fetched
  const otherMarketLeaderMetricsData = marketLeaderHooksArray
    .filter((hook) => hook && hook.id && hook.isOther && hook.shouldFetch)
    .map((hook, idx) => ({
      ...hook,
      index: idx, // Keep track of the index for datasetId calculation
    }))

  const isLoadingOtherMarketLeaderMetrics = otherMarketLeaderMetricsData.some(
    ({ isLoading }) => isLoading,
  )

  const mainLocationCount = metricsData?.locations?.length ?? 1
  const otherLocationCounts = otherMarketLeaderMetricsData.map(
    ({ data }) => data?.locations?.length ?? 1,
  )

  // Reset pagination when data changes
  useEffect(() => {
    setPage(1)
  }, [startDate, endDate, queryFilter])

  // Prepare table data
  const tableData = useMemo(() => {
    const result: LocationMetricRow[] = []

    // Process main metrics data
    const mainRows = processMetricsData(
      metricsData,
      locationsMap,
      marketLeadersLocationGroupsMap[locationGroupId as number]?.name ??
        'TOTAL',
      1,
      startDate,
      laborMetricsData,
    )
    result.push(...mainRows)

    // Process all other market leaders' data if showOtherMarketLeaders is true
    if (showOtherMarketLeaders) {
      otherMarketLeaderMetricsData.forEach((mlData, index) => {
        const { id, data, laborData } = mlData
        // Process even if locations array is empty - the summary might still have data
        if (data) {
          const otherRows = processMetricsData(
            data,
            locationsMap,
            marketLeadersLocationGroupsMap[id]?.name ?? 'TOTAL',
            index + 2, // Start dataset IDs from 2 for other market leaders
            startDate,
            laborData,
          )
          result.push(...otherRows)
        }
      })
    }

    return result
  }, [
    metricsData,
    locationsMap,
    showOtherMarketLeaders,
    otherMarketLeaderMetricsData,
    locationGroupId,
    marketLeadersLocationGroupsMap,
    startDate,
    laborMetricsData,
  ])

  // Handle sorting
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sortedData = useMemo(() => {
    return sortTableData(tableData, orderBy, order)
  }, [tableData, order, orderBy])

  // Handle pagination
  const handleChangePage = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage)
  }

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * ROWS_PER_PAGE
    return sortedData.slice(startIndex, startIndex + ROWS_PER_PAGE)
  }, [sortedData, page])

  if (
    isLoadingLocations ||
    isLoadingLaborCoverage ||
    isLoadingLaborMetrics ||
    isLoadingMetrics ||
    isLoadingLocationGroups ||
    isLoadingLocationMappings ||
    isLoadingOtherMarketLeaderMetrics
  ) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    )
  }

  // Format end date for display
  const updateDate = getUpdateDate(endDate, coveredEndDate)
  const formattedUpdateDate = updateDate
    ? moment(updateDate).format('M/D/YY')
    : ''

  // Handle CSV download
  const handleDownloadCSV = () => {
    downloadCSV(
      sortedData,
      formattedUpdateDate,
      mainLocationCount,
      otherLocationCounts,
      startDate,
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body1" sx={{ fontFamily: 'Lexend-Regular' }}>
          Numbers refresh the Wednesday after the previous week closes. Last
          updated on {formattedUpdateDate}.
        </Typography>
      </Box>

      <Box sx={{ padding: '5px 0 20px 0' }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          size="small"
          onClick={handleDownloadCSV}
          sx={{ ml: 2, fontFamily: 'Lexend-Regular' }}
        >
          Download as CSV
        </Button>
      </Box>

      <Box sx={{ overflowX: 'auto', mb: 4 }}>
        <MetricsTable
          data={paginatedData}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          targetVersion={targetVersion}
          mainLocationCount={mainLocationCount}
          otherLocationCount={otherLocationCounts[0] ?? 1}
          otherLocationCounts={otherLocationCounts}
          useNewRounding={useNewRounding}
          startDate={startDate}
        />

        {sortedData.length > ROWS_PER_PAGE && (
          <Box display="flex" justifyContent="center" mt={1}>
            <Pagination
              count={Math.ceil(sortedData.length / ROWS_PER_PAGE)}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size="small"
              siblingCount={isMobile ? 0 : 1}
            />
          </Box>
        )}

        {isMarketLeaderSelected ? (
          <Box mt={2}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setShowOtherMarketLeaders(!showOtherMarketLeaders)}
              sx={{ fontFamily: 'Lexend-Regular' }}
            >
              {showOtherMarketLeaders ? 'Hide' : 'Show'} Other Market Leaders
            </Button>
          </Box>
        ) : null}
      </Box>

      <BonusPotentialTable />
      <DisqualifiersSection
        targetVersion={targetVersion}
        startDate={startDate}
      />
    </Box>
  )
}

export default MwbBonusTracker
